import { useState, useEffect } from "react";
import { useAuth } from "contexts/AuthContext";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import PropTypes from "prop-types";
import Swal from "sweetalert2";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import PersonIcon from "@mui/icons-material/Person";
import StatusChip from "components/StatusChip";
import { format, startOfDay, endOfDay, subDays } from "date-fns";
import { API_BASE, API_HOST } from "config/api";

// Define prop types for row data
const rowPropTypes = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  serial: PropTypes.number,
  memberName: PropTypes.string,
  isCheckedIn: PropTypes.bool,
  checkedInAt: PropTypes.string,
  method: PropTypes.string,
  nationality: PropTypes.string,
  phone: PropTypes.string,
  email: PropTypes.string,
  originalData: PropTypes.object,
});

// Cell Components
const SerialCell = ({ value }) => (
  <Typography variant="caption" color="text.secondary" fontWeight={600}>
    {value}
  </Typography>
);
SerialCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

const MemberCell = ({ value, row }) => (
  <Box display="flex" alignItems="center" gap={1.5}>
    <Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>{value?.charAt(0)}</Avatar>
    <Box>
      <Typography variant="button" fontWeight={600} display="block">
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {row.original.nationality || "Member"}
      </Typography>
    </Box>
  </Box>
);
MemberCell.propTypes = {
  value: PropTypes.string.isRequired,
  row: PropTypes.shape({
    original: PropTypes.shape({
      nationality: PropTypes.string,
    }),
  }).isRequired,
};

const AttendanceStatusCell = ({ value }) => (
  <Box display="flex" justifyContent="center">
    {value ? (
      <CheckIcon color="success" fontSize="medium" />
    ) : (
      <CloseIcon color="error" fontSize="medium" />
    )}
  </Box>
);
AttendanceStatusCell.propTypes = {
  value: PropTypes.bool.isRequired,
};

const DateTimeCell = ({ value }) => (
  <Typography variant="caption" color="text.secondary" fontWeight={600}>
    {value === "Not checked in" || value === "Absent" ? value : new Date(value).toLocaleString()}
  </Typography>
);
DateTimeCell.propTypes = {
  value: PropTypes.string.isRequired,
};

const MethodCell = ({ value }) => (
  <Box ml={-0.5}>
    <StatusChip
      value={value}
      color={value === "biometric" ? "success" : value === "manual" ? "info" : "warning"}
    />
  </Box>
);
MethodCell.propTypes = {
  value: PropTypes.string.isRequired,
};

const PhoneCell = ({ value }) => (
  <Typography variant="caption" color="text.secondary" fontWeight={600}>
    {value || "N/A"}
  </Typography>
);
PhoneCell.propTypes = {
  value: PropTypes.string,
};

const EmailCell = ({ value }) => (
  <Typography variant="caption" color="text.secondary" fontWeight={600}>
    {value || "N/A"}
  </Typography>
);
EmailCell.propTypes = {
  value: PropTypes.string,
};

const ActionsCell = ({ row, takeAttendance, takeManualAttendance }) => {
  const isCheckedIn = row.original.isCheckedIn;
  const isAbsent = row.original.checkedInAt === "Absent";

  return (
    <Box display="flex" justifyContent="center" gap={1}>
      <Button
        size="small"
        variant="contained"
        color={isCheckedIn ? "info" : isAbsent ? "warning" : "success"}
        startIcon={<FingerprintIcon fontSize="small" />}
        onClick={() => !isAbsent && takeAttendance(row.original)}
        disabled={isCheckedIn || isAbsent}
      >
        {isAbsent ? "Absent" : isCheckedIn ? "Checked In" : "Check In"}
      </Button>
      <Button
        size="small"
        variant="contained"
        color={isCheckedIn ? "info" : isAbsent ? "warning" : "primary"}
        startIcon={<PersonIcon fontSize="small" />}
        onClick={() => !isAbsent && takeManualAttendance(row.original)}
        disabled={isCheckedIn || isAbsent}
      >
        {isAbsent ? "Absent" : isCheckedIn ? "Checked In" : "Manual"}
      </Button>
    </Box>
  );
};

ActionsCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      isCheckedIn: PropTypes.bool.isRequired,
      checkedInAt: PropTypes.string.isRequired,
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      first_name: PropTypes.string,
      surname: PropTypes.string,
      nationality: PropTypes.string,
      phone: PropTypes.string,
      email: PropTypes.string,
    }).isRequired,
  }).isRequired,
  takeAttendance: PropTypes.func.isRequired,
  takeManualAttendance: PropTypes.func.isRequired,
};

// Main Hook
export default function useAttendanceData() {
  const { church } = useAuth();
  const [showOnlyUnchecked, setShowOnlyUnchecked] = useState(false);
  const [showAbsentMembers, setShowAbsentMembers] = useState(false);
  const [allMembers, setAllMembers] = useState([]);
  const [dateRange, setDateRange] = useState({
    startDate: startOfDay(subDays(new Date(), 365)),
    endDate: endOfDay(new Date()),
  });
  const [manualAttendanceDialog, setManualAttendanceDialog] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const [attendanceData, setAttendanceData] = useState({
    columns: [
      { Header: "SN", accessor: "serial", align: "center", width: "5%", Cell: SerialCell },
      { Header: "Member", accessor: "memberName", align: "left", width: "25%", Cell: MemberCell },
      { Header: "Phone", accessor: "phone", align: "left", width: "15%", Cell: PhoneCell },
      { Header: "Email", accessor: "email", align: "left", width: "15%", Cell: EmailCell },
      {
        Header: "Checked In",
        accessor: "isCheckedIn",
        align: "center",
        width: "10%",
        Cell: AttendanceStatusCell,
      },
      {
        Header: "Checked In At",
        accessor: "checkedInAt",
        align: "left",
        width: "15%",
        Cell: DateTimeCell,
      },
      {
        Header: "Method",
        accessor: "method",
        align: "center",
        width: "10%",
        Cell: MethodCell,
      },
      {
        Header: "Actions",
        accessor: "actions",
        align: "center",
        width: "15%",
        Cell: (props) => (
          <ActionsCell
            {...props}
            takeAttendance={takeAttendance}
            takeManualAttendance={takeManualAttendance}
          />
        ),
      },
    ],
    rows: [],
    isLoading: true,
    error: null,
  });

  const fetchAllMembers = async () => {
    try {
      if (!church?.id) {
        throw new Error("Church context is not available");
      }

      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("Authentication token missing");
      }

      const response = await fetch(`${API_BASE}/members/church/${church.id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch members");
      }

      const result = await response.json();
      const members = result || result.members || [];
      console.log("Fetched members:", members);

      if (!Array.isArray(members)) {
        throw new Error("Invalid members data format");
      }

      setAllMembers(members);
    } catch (error) {
      console.error("Failed to fetch members:", error);
      setAttendanceData((prev) => ({
        ...prev,
        error: error.message,
      }));
      Swal.fire({
        title: "Error!",
        text: error.message || "Failed to load members",
        icon: "error",
      });
    }
  };

  const fetchAttendanceData = async (startDate = null, endDate = null) => {
    try {
      setAttendanceData((prev) => ({ ...prev, isLoading: true, error: null }));

      if (!church?.id) {
        throw new Error("Church ID is required");
      }

      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("Authentication token missing");
      }

      // Use explicit dates if provided; fall back to dateRange only when both are given.
      const effectiveStartDate = startDate !== undefined ? startDate : null;
      const effectiveEndDate = endDate !== undefined ? endDate : null;

      const formatDate = (date) => (date ? format(date, "yyyy-MM-dd") : null);
      const start = formatDate(effectiveStartDate);
      const end = formatDate(effectiveEndDate);

      let url = `${API_BASE}/attendance/${church.id}`;
      const params = new URLSearchParams();

      // Only send date params when both are specified — omitting them returns all records.
      if (start && end) {
        params.append("start_date", start);
        params.append("end_date", end);
      }

      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await fetch(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch attendance data");
      }

      const result = await response.json();
      const attendanceRecords = result.attendance || result.data || [];

      console.log(result);

      if (!Array.isArray(attendanceRecords)) {
        throw new Error("Invalid attendance data format");
      }

      const rows = attendanceRecords.map((record, index) => ({
        id: record.member_id || record.id,
        serial: index + 1,
        memberName:
          record.member_name ||
          `${record.first_name || ""} ${record.surname || ""}`.trim() ||
          "Unknown Member",
        isCheckedIn: record.status === "Check-in",
        checkedInAt:
          record.checked_in_at || record.check_time || record.timestamp || "Not checked in",
        method: record.method || "biometric",
        nationality: record.nationality || "Not specified",
        phone: record.phone || null,
        email: record.email || null,
        originalData: record,
      }));

      setAttendanceData((prev) => ({
        ...prev,
        rows,
        isLoading: false,
      }));
    } catch (error) {
      console.error("Fetch failed:", error);
      setAttendanceData((prev) => ({
        ...prev,
        isLoading: false,
        error: error.message,
      }));
      Swal.fire({
        title: "Error!",
        text: error.message || "Failed to load attendance data",
        icon: "error",
      });
    }
  };

  const takeAttendance = async (memberData) => {
    try {
      if (!memberData?.id) {
        throw new Error("Invalid member data");
      }

      if (!church?.id) {
        throw new Error("Church context is not available");
      }

      setAttendanceData((prev) => ({ ...prev, isLoading: true }));

      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("Authentication token missing");
      }

      const response = await fetch(`${API_BASE}/attendance`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          member_id: memberData.id,
          method: "biometric",
          church_id: church.id,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Attendance operation failed");
      }

      await Swal.fire({
        title: "Success!",
        text: "Attendance recorded successfully",
        icon: "success",
      });

      fetchAttendanceData(startOfDay(new Date()), endOfDay(new Date()));
    } catch (error) {
      console.error("Attendance error:", error);
      setAttendanceData((prev) => ({
        ...prev,
        isLoading: false,
      }));
      Swal.fire({
        title: "Error!",
        text: error.message || "Failed to record attendance",
        icon: "error",
      });
    }
  };

  const takeManualAttendance = async (memberData = null) => {
    try {
      const memberId = memberData?.id || selectedMember?.id;

      if (!memberId) {
        throw new Error("Please select a member");
      }

      if (!church?.id) {
        throw new Error("Church context is not available");
      }

      setAttendanceData((prev) => ({ ...prev, isLoading: true }));

      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("Authentication token missing");
      }

      const response = await fetch(`${API_BASE}/attendance`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          member_id: memberId,
          checked_in_at: new Date().toISOString(),
          method: "manual",
          church_id: church.id,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Manual attendance operation failed");
      }

      await Swal.fire({
        title: "Success!",
        text: "Manual attendance recorded successfully",
        icon: "success",
      });

      // Close dialog and reset state if it was opened
      if (manualAttendanceDialog) {
        setManualAttendanceDialog(false);
        setSelectedMember(null);
        setSearchQuery("");
      }

      fetchAttendanceData(startOfDay(new Date()), endOfDay(new Date()));
    } catch (error) {
      console.error("Manual attendance error:", error);
      setAttendanceData((prev) => ({
        ...prev,
        isLoading: false,
      }));
      Swal.fire({
        title: "Error!",
        text: error.message || "Failed to record manual attendance",
        icon: "error",
      });
    }
  };

  const handleShowAbsent = () => {
    const newShowAbsent = !showAbsentMembers;
    setShowAbsentMembers(newShowAbsent);

    if (newShowAbsent) {
      const presentMemberIds = attendanceData.rows.map((row) => row.id);
      const absentMembers = allMembers
        .filter((member) => !presentMemberIds.includes(member.id))
        .map((member, index) => ({
          id: member.id,
          serial: attendanceData.rows.length + index + 1,
          memberName: `${member.first_name} ${member.surname}`,
          isCheckedIn: false,
          checkedInAt: "Absent",
          method: "N/A",
          nationality: member.nationality || "Not specified",
          phone: member.phone || null,
          email: member.email || null,
          originalData: member,
        }));

      setAttendanceData((prev) => ({
        ...prev,
        rows: [...prev.rows, ...absentMembers],
      }));
    } else {
      fetchAttendanceData(startOfDay(new Date()), endOfDay(new Date()));
    }
  };

  // Filter members for search
  const filteredMembers = searchQuery
    ? allMembers.filter(
        (member) =>
          `${member.first_name} ${member.surname}`
            .toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          (member.phone && member.phone.toLowerCase().includes(searchQuery.toLowerCase())) ||
          (member.email && member.email.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    : allMembers;

  useEffect(() => {
    if (church?.id) {
      fetchAllMembers();
      fetchAttendanceData(); // no dates → backend returns all records
    }
  }, [church]);

  const filteredRows = showOnlyUnchecked
    ? attendanceData.rows.filter((row) => !row.isCheckedIn)
    : attendanceData.rows;

  return {
    ...attendanceData,
    rows: filteredRows,
    showOnlyUnchecked,
    setShowOnlyUnchecked,
    showAbsentMembers,
    setShowAbsentMembers,
    selectedDate: dateRange.startDate,
    handleDateChange: (date) => {
      const start = startOfDay(date);
      const end = endOfDay(date);
      setDateRange({ startDate: start, endDate: end });
      fetchAttendanceData(start, end);
    },
    fetchAttendanceData,
    handleShowAbsent,
    takeAttendance,
    takeManualAttendance,
    // Manual attendance properties
    manualAttendanceDialog,
    setManualAttendanceDialog,
    selectedMember,
    setSelectedMember,
    searchQuery,
    setSearchQuery,
    filteredMembers,
  };
}
